import {EditorState, StateEffect, StateField, Transaction} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'

const updateRegistryEffect = StateEffect.define<[url: string, validity: 'valid' | 'invalid']>()

const registryField = StateField.define<ReadonlyMap<string, 'valid' | 'invalid'>>({
  create: () => new Map(),
  update(registry, tr) {
    let result = registry
    for (const effect of tr.effects) {
      if (effect.is(updateRegistryEffect)) {
        result = new Map([...registry, effect.value])
      }
    }
    return result
  },
})

export function isKnownInvalidVideoUrl(state: EditorState, url: string) {
  return state.field(registryField).get(url) === 'invalid'
}

export function isKnownValidVideoUrl(state: EditorState, url: string) {
  return state.field(registryField).get(url) === 'valid'
}

/** Register an invalid video URL on error. A known invalid URL will not be checked again. */
export function registerInvalidVideoUrl(view: EditorView, url: string) {
  view.dispatch({
    effects: [updateRegistryEffect.of([url, 'invalid'])],
  })
}

/**
 * Register a known valid video URL. Known valid URLs will not render the plaintext loading placeholder, which avoids
 * flickering on completed video uploads / already-checked videos.
 */
export function registerValidVideoUrl(view: EditorView, url: string) {
  view.dispatch({
    effects: [updateRegistryEffect.of([url, 'valid'])],
  })
}

export function videoRegistryChanged(tr: Transaction) {
  return tr.state.field(registryField) !== tr.startState.field(registryField)
}

/**
 * Tracks a registry of URLs we have attempted to render as videos. Valid URLs are confirmed accessible videos, while
 * invalid URLs are either inaccessible, invalid, or just not video files.
 */
export const videoRegistryExtension = [registryField]
